// Backend-API-client. Alle endpoints zijn relatief (/api/...) zodat ze
// zowel via de Vite-dev-proxy als via de productie-server werken.
//
// We sturen `credentials: "include"` mee zodat de session-cookie correct
// terugkomt op subsequente requests (HttpOnly + SameSite=Lax).

import type { Scenario } from "../types/scenario";

export interface AuthUser {
  id: number;
  username: string;
  isAdmin: boolean;
}

export interface ScenarioSummary {
  id: string;
  name: string;
  description: string;
  author: string;
  createdAt: string;
  updatedAt: string;
}

export interface ApiScenario extends ScenarioSummary {
  data: Scenario;
}

class ApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(`/api${path}`, {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(init?.headers ?? {}),
    },
    ...init,
  });
  if (!res.ok) {
    let body: unknown = null;
    try {
      body = await res.json();
    } catch {
      // ignore
    }
    const message =
      (body && typeof body === "object" && "error" in body
        ? String((body as { error: unknown }).error)
        : null) ?? `HTTP ${res.status}`;
    throw new ApiError(message, res.status);
  }
  if (res.status === 204) return undefined as unknown as T;
  return (await res.json()) as T;
}

// ---- Auth ----

export async function login(username: string, password: string): Promise<AuthUser> {
  const r = await request<{ user: AuthUser }>("/auth/login", {
    method: "POST",
    body: JSON.stringify({ username, password }),
  });
  return r.user;
}

export async function logout(): Promise<void> {
  await request("/auth/logout", { method: "POST" });
}

export async function getCurrentUser(): Promise<AuthUser | null> {
  const r = await request<{ user: AuthUser | null }>("/auth/me");
  return r.user;
}

// ---- Scenarios ----

export async function listPublicScenarios(): Promise<ScenarioSummary[]> {
  const r = await request<{ scenarios: ScenarioSummary[] }>("/scenarios");
  return r.scenarios;
}

export async function fetchPublicScenario(id: string): Promise<ApiScenario> {
  const r = await request<{ scenario: ApiScenario }>(`/scenarios/${encodeURIComponent(id)}`);
  return r.scenario;
}

export async function createPublicScenario(scenario: Scenario): Promise<void> {
  await request("/scenarios", {
    method: "POST",
    body: JSON.stringify({
      id: scenario.id,
      name: scenario.name,
      description: scenario.description ?? "",
      author: scenario.author ?? "",
      data: scenario,
    }),
  });
}

export async function updatePublicScenario(scenario: Scenario): Promise<void> {
  await request(`/scenarios/${encodeURIComponent(scenario.id)}`, {
    method: "PUT",
    body: JSON.stringify({
      id: scenario.id,
      name: scenario.name,
      description: scenario.description ?? "",
      author: scenario.author ?? "",
      data: scenario,
    }),
  });
}

export async function deletePublicScenario(id: string): Promise<void> {
  await request(`/scenarios/${encodeURIComponent(id)}`, { method: "DELETE" });
}

// ---- Settings ----

export interface AppSettings {
  defaultScenarioId: string | null;
  logoUrl: string | null;
}

export async function getAppSettings(): Promise<AppSettings> {
  return await request<AppSettings>("/settings");
}

export async function updateAppSettings(
  patch: Partial<AppSettings>,
): Promise<AppSettings> {
  return await request<AppSettings>("/settings", {
    method: "PUT",
    body: JSON.stringify(patch),
  });
}

export async function setDefaultScenarioId(id: string | null): Promise<void> {
  await updateAppSettings({ defaultScenarioId: id });
}

export async function setLogoUrl(url: string | null): Promise<AppSettings> {
  return await updateAppSettings({ logoUrl: url });
}

export { ApiError };
